// Single source of truth for the human-readable location string RDrive shows
// (and that spreadsheets exported from it carry):
//
//   "<title> (Drawing:<drawingTitle>, Drawing Set:<drawingSetTitle>)"
//
// App.vue builds resourceStore.locationsList with it, and RPilotPage hands the
// same string to the browser sandbox as `location.displayTitle` — a transform
// matching a sheet column against project locations must be comparing the exact
// same text, so the format cannot live in two places.
//
// Duplicate titles under one drawing get their x,y appended (the only way the
// picker can tell them apart); that variant is opt-in via `withCoordinates`,
// because it is a disambiguation for humans, not part of the base format.

/**
 * @param {Object}  location                 { title, drawingTitle, x, y }
 * @param {string}  drawingSetTitle          Resolved drawing-set title.
 * @param {Object} [options]
 * @param {boolean} [options.withCoordinates] Append ", x:…, y:…" when the
 *                                            location has coordinates.
 * @returns {string}
 */
export function formatLocationDisplayTitle(location, drawingSetTitle, { withCoordinates = false } = {}) {
  const base = `${location?.title} (Drawing:${location?.drawingTitle}, Drawing Set:${drawingSetTitle})`;
  if (!withCoordinates || location?.x == null || location?.y == null) return base;
  return `${location.title} (Drawing:${location.drawingTitle}, Drawing Set:${drawingSetTitle}, `
    + `x:${Math.round(location.x)}, y:${Math.round(location.y)})`;
}

/**
 * Title lookup for drawing sets, keyed by id. Accepts the store's array (or the
 * single-object shape the API sometimes returns, already normalised upstream).
 */
export function drawingSetTitleById(drawingSets) {
  return new Map((drawingSets || []).filter(Boolean).map((set) => [set.id, set.title]));
}

/**
 * Project the resource store's locations down to what the RPilot browser
 * sandbox exposes as its `locations` global. Kept here (rather than inline in
 * RPilotPage) so the shape the agent's scripts depend on is unit-testable and
 * shares the display formatter with locationsList.
 *
 * @param {Array} locations   resourceStore.getLocations
 * @param {Array} drawingSets resourceStore.getDrawingSets
 * @returns {Array<{ id, title, drawingId, drawingTitle, drawingSetId,
 *                   drawingSetTitle: string|null, displayTitle: string|null }>}
 */
export function projectSandboxLocations(locations, drawingSets) {
  const titles = drawingSetTitleById(drawingSets);
  return (locations || []).map((location) => {
    const drawingSetTitle = titles.get(location.drawingSetId) ?? null;
    return {
      id: location.id,
      title: location.title,
      drawingId: location.drawingId,
      drawingTitle: location.drawingTitle,
      drawingSetId: location.drawingSetId,
      drawingSetTitle,
      // The exact string RDrive shows for this location, so a transform can
      // match a spreadsheet column like
      // "CCT-B1-143 (Drawing:L1, Drawing Set:Arch)" against it directly.
      // Null when the drawing set isn't loaded — which is also when
      // locationsList omits the location altogether.
      displayTitle: drawingSetTitle === null
        ? null
        : formatLocationDisplayTitle(location, drawingSetTitle),
    };
  });
}
